import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useTheme } from '../../../hooks/useTheme';
import Card3D from '../../../components/base/Card3D';
import ParticleBackground from '../../../components/base/ParticleBackground';
import AnimatedCounter from '../../../components/base/AnimatedCounter';

export default function ConsensusSection() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: mainRef, isVisible: mainVisible } = useScrollAnimation();
  const { ref: bottomRef, isVisible: bottomVisible } = useScrollAnimation();

  return (
    <section
      id="consensus"
      className={`py-16 md:py-24 lg:py-28 relative overflow-hidden transition-colors duration-500 ${
        isDark ? 'bg-[#0d1117]' : 'bg-white'
      }`}
    >
      {/* 背景装饰 */}
      <div
        className={`absolute top-20 left-0 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-gradient-radial ${
          isDark ? 'from-[#E84A1F]/5' : 'from-[#E84A1F]/3'
        } via-transparent to-transparent blur-3xl`}
      ></div>
      <div
        className={`absolute bottom-0 right-0 w-[200px] md:w-[400px] h-[200px] md:h-[400px] bg-gradient-radial ${
          isDark ? 'from-[#F06030]/5' : 'from-[#F06030]/3'
        } via-transparent to-transparent blur-3xl`}
      ></div>

      {/* 中心渐变光晕 */}
      {/* Fixed: removed invalid “/[0.03]” syntax that caused a parsing error */}
      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] md:w-[900px] h-[500px] md:h-[900px] bg-gradient-radial ${
          isDark ? 'from-[#E84A1F]/[0.03]' : 'from-[#E84A1F]/[0.02]'
        } via-transparent to-transparent blur-[100px]`}
      ></div>

      {/* 顶部渐变带 */}
      <div
        className={`absolute top-0 left-0 right-0 h-[250px] md:h-[400px] bg-gradient-to-b ${
          isDark ? 'from-[#161b22]/80' : 'from-[#f8fafb]/80'
        } to-transparent`}
      ></div>

      {/* 网格背景 */}
      <div
        className={`absolute inset-0 ${
          isDark ? 'opacity-[0.05]' : 'opacity-[0.03]'
        }`}
        style={{
          backgroundImage: `linear-gradient(${
            isDark ? 'rgba(255,255,255,0.5)' : 'rgba(13,17,23,0.5)'
          } 1px, transparent 1px), linear-gradient(90deg, ${
            isDark ? 'rgba(255,255,255,0.5)' : 'rgba(13,17,23,0.5)'
          } 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }}
      ></div>

      {/* 盾牌/安全风格六边形网格 */}
      <div
        className="absolute inset-0 opacity-[0.012]"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg width=\'50\' height=\'50\' viewBox=\'0 0 50 50\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M25 2l20 10v16c0 10-8 18-20 22-12-4-20-12-20-22V12z\' fill=\'none\' stroke=\'%23E84A1F\' stroke-width=\'0.5\'/%3E%3C/svg%3E")',
          backgroundSize: '80px 80px',
        }}
      ></div>

      {/* 装饰性圆环 */}
      <div
        className={`absolute top-[8%] right-[10%] w-[120px] md:w-[220px] h-[120px] md:h-[220px] border ${
          isDark ? 'border-[#E84A1F]/10' : 'border-[#E84A1F]/5'
        } rounded-full hidden md:block`}
      ></div>
      <div
        className={`absolute bottom-[12%] left-[5%] w-[100px] md:w-[180px] h-[100px] md:h-[180px] border border-dashed ${
          isDark ? 'border-[#F06030]/10' : 'border-[#F06030]/5'
        } rounded-full hidden md:block`}
      ></div>

      {/* 连接线装饰 */}
      <div
        className={`absolute top-[30%] left-0 w-[150px] md:w-[300px] h-px bg-gradient-to-r ${
          isDark ? 'from-[#E84A1F]/20' : 'from-[#E84A1F]/10'
        } to-transparent hidden md:block`}
      ></div>
      <div
        className={`absolute bottom-[40%] right-0 w-[100px] md:w-[200px] h-px bg-gradient-to-l ${
          isDark ? 'from-[#F06030]/20' : 'from-[#F06030]/10'
        } to-transparent hidden md:block`}
      ></div>

      {/* 浮动节点装饰 */}
      <div
        className={`absolute top-[15%] left-[15%] w-3 h-3 ${
          isDark ? 'bg-[#E84A1F]/20' : 'bg-[#E84A1F]/10'
        } rounded-full hidden lg:block`}
      ></div>
      <div
        className={`absolute top-[40%] right-[8%] w-2 h-2 ${
          isDark ? 'bg-[#F06030]/25' : 'bg-[#F06030]/15'
        } rounded-full hidden lg:block`}
      ></div>
      <div
        className={`absolute bottom-[25%] left-[10%] w-4 h-4 border ${
          isDark ? 'border-[#E84A1F]/20' : 'border-[#E84A1F]/10'
        } rounded-full hidden lg:block`}
      ></div>
      <div
        className={`absolute bottom-[35%] right-[18%] w-2 h-2 ${
          isDark ? 'bg-[#E84A1F]/20' : 'bg-[#E84A1F]/10'
        } rounded-full hidden lg:block`}
      ></div>

      {/* 粒子背景效果 */}
      <ParticleBackground 
        particleCount={55} 
        speed={0.35} 
        connectDistance={95}
      />

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div
          ref={headerRef}
          className={`flex flex-col md:flex-row items-start gap-4 md:gap-8 mb-10 md:mb-16 relative transition-all duration-1000 ${
            headerVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-10'
          }`}
        >
          {/* 装饰性大号数字 */}
          <div
            className={`absolute -top-8 left-0 text-[100px] md:text-[180px] font-bold ${
              isDark
                ? 'text-white/[0.02]'
                : 'text-[#0d1117]/[0.02]'
            } leading-none select-none pointer-events-none hidden md:block`}
          >
            03
          </div>

          {/* 左侧编号栏 */}
          <div className="flex md:flex-col items-center gap-3 pt-0 md:pt-2">
            <div className="w-10 md:w-12 h-10 md:h-12 bg-gradient-to-br from-[#E84A1F] to-[#C83A15] rounded-xl flex items-center justify-center shadow-lg shadow-[#E84A1F]/20">
              <span className="text-white font-bold text-base md:text-lg">03</span>
            </div>
            <div
              className={`hidden md:block w-px bg-gradient-to-b from-[#E84A1F] to-transparent transition-all duration-1000 delay-300 ${
                headerVisible ? 'h-16' : 'h-0'
              }`}
            ></div>
          </div>

          {/* 标题内容 */}
          <div className="flex-1 flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-0">
            <div className="relative">
              <div className="inline-flex items-center gap-3 mb-3 md:mb-4">
                <div className="w-2 h-2 bg-[#E84A1F] rounded-full animate-pulse"></div>
                <span className="text-xs text-[#E84A1F] tracking-[0.2em] md:tracking-[0.3em] uppercase">
                  {t('consensus.badge')}
                </span>
              </div>
              <h2
                className={`text-3xl md:text-4xl lg:text-5xl font-bold transition-colors duration-500 ${
                  isDark ? 'text-white' : 'text-[#0d1117]'
                }`}
              >
                {t('consensus.title')}
              </h2>
            </div>

            <div
              className={`max-w-full md:max-w-[380px] text-sm leading-relaxed pb-0 md:pb-2 border-l-0 md:border-l border-[#E84A1F]/30 pl-0 md:pl-6 transition-all duration-1000 delay-500 ${
                isDark ? 'text-gray-400' : 'text-[#0d1117]/50'
              } ${headerVisible ? 'opacity-100 translate-x-0' : 'opacity-0 md:translate-x-10'}`}
            >
              {t('consensus.subtitle')}
            </div>
          </div>
        </div>

        {/* 主内容区 */}
        <div ref={mainRef} className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
          {/* 左侧大卡片 - 共识机制 */}
          <div
            className={`lg:col-span-7 relative group transition-all duration-1000 ${
              mainVisible ? 'opacity-100 translate-x-0' : 'opacity-0 lg:-translate-x-10'
            }`}
          >
            <Card3D className="h-full" intensity={8} scale={1.02}>
              <div className="absolute inset-0 bg-gradient-to-br from-[#E84A1F]/10 to-transparent rounded-xl md:rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div
                className={`relative border rounded-xl md:rounded-2xl p-5 md:p-10 h-full hover:border-[#E84A1F]/30 transition-all duration-500 shadow-lg ${
                  isDark
                    ? 'bg-[#161b22] border-white/10 shadow-black/20'
                    : 'bg-[#f8fafb] border-[#0d1117]/10 shadow-[#0d1117]/5'
                }`}
              >
                {/* 角标装饰 */}
                <div className="absolute top-0 right-0 w-24 md:w-32 h-24 md:h-32 overflow-hidden rounded-tr-2xl hidden md:block">
                  <div
                    className={`absolute top-4 right-4 w-16 md:w-20 h-16 md:h-20 border ${
                      isDark ? 'border-[#E84A1F]/20' : 'border-[#E84A1F]/10'
                    } rounded-full`}
                  ></div>
                  <div
                    className={`absolute top-6 right-6 w-10 md:w-12 h-10 md:h-12 border ${
                      isDark ? 'border-[#E84A1F]/30' : 'border-[#E84A1F]/20'
                    } rounded-full`}
                  ></div>
                </div>

                <div className="flex items-start gap-4 md:gap-6">
                  <div className="w-12 md:w-16 h-12 md:h-16 bg-gradient-to-br from-[#E84A1F] to-[#C83A15] rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg shadow-[#E84A1F]/20 flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <h3
                      className={`text-xl md:text-2xl font-bold mb-1 md:mb-2 transition-colors duration-500 ${
                        isDark ? 'text-white' : 'text-[#0d1117]'
                      }`}
                    >
                      {t('consensus.mechanism.title')}
                    </h3>
                    <div className="w-12 md:w-16 h-1 bg-gradient-to-r from-[#E84A1F] to-transparent rounded-full mb-3 md:mb-4"></div>
                  </div>
                </div>

                <div className="mt-4 md:mt-6 space-y-3 md:space-y-4">
                  {[t('consensus.mechanism.desc1'), t('consensus.mechanism.desc2'), t('consensus.mechanism.desc3')].map(
                    (desc, index) => (
                      <div
                        key={index}
                        className={`flex items-start gap-2.5 md:gap-3 group/item transition-all duration-500 ${
                          mainVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                        }`}
                        style={{ transitionDelay: `${300 + index * 100}ms` }}
                      >
                        <div className="w-5 md:w-6 h-5 md:h-6 bg-[#E84A1F]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:bg-[#E84A1F]/20 transition-colors">
                          <span className="text-[#E84A1F] text-[10px] md:text-xs font-bold">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                        </div>
                        <p className={`text-xs md:text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-[#0d1117]/60'}`}>{desc}</p>
                      </div>
                    )
                  )}
                </div>

                {/* 进度指示器 */}
                <div
                  className={`mt-6 md:mt-8 pt-4 md:pt-6 border-t ${
                    isDark ? 'border-white/10' : 'border-[#0d1117]/10'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <span
                      className={`text-[10px] md:text-xs uppercase tracking-wider ${
                        isDark ? 'text-gray-500' : 'text-[#0d1117]/40'
                      }`}
                    >
                      {t('consensus.mechanism.metric')}
                    </span>
                    <span className="text-xl md:text-2xl font-bold text-[#E84A1F]">
                      <AnimatedCounter end={95} suffix="%" duration={2000} />
                    </span>
                  </div>
                  <div
                    className={`h-1.5 md:h-2 rounded-full overflow-hidden ${
                      isDark ? 'bg-white/5' : 'bg-[#0d1117]/5'
                    }`}
                  >
                    <div className="h-full bg-gradient-to-r from-[#E84A1F] via-[#F06030] to-[#E84A1F] rounded-full relative" style={{ width: '95%' }}>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-[shimmer_2s_infinite]"></div>
                    </div>
                  </div>
                </div>
              </div>
            </Card3D>
          </div>

          {/* 右侧堆叠卡片 */}
          <div className="lg:col-span-5 flex flex-col gap-4 md:gap-6">
            {/* 性能卡片 */}
            <div
              className={`relative group flex-1 transition-all duration-700 ${
                mainVisible ? 'opacity-100 translate-x-0' : 'opacity-0 lg:translate-x-10'
              }`}
              style={{ transitionDelay: '200ms' }}
            >
              <Card3D className="h-full" intensity={10} scale={1.03}>
                <div className="absolute inset-0 bg-gradient-to-br from-[#F06030]/10 to-transparent rounded-xl md:rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div
                  className={`relative border rounded-xl md:rounded-2xl p-4 md:p-6 h-full hover:border-[#E84A1F]/30 transition-all duration-500 shadow-lg ${
                    isDark
                      ? 'bg-[#161b22] border-white/10 shadow-black/20'
                      : 'bg-[#f8fafb] border-[#0d1117]/10 shadow-[#0d1117]/5'
                  }`}
                >
                  <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
                    <div className="w-10 md:w-12 h-10 md:h-12 bg-gradient-to-br from-[#F06030] to-[#E84A1F] rounded-lg md:rounded-xl flex items-center justify-center">
                      <i className="ri-speed-line text-xl md:text-2xl text-white"></i>
                    </div>
                    <h3
                      className={`text-base md:text-lg font-semibold transition-colors duration-500 ${
                        isDark ? 'text-white' : 'text-[#0d1117]'
                      }`}
                    >
                      {t('consensus.performance.title')}
                    </h3>
                  </div>

                  <p className={`text-xs md:text-sm leading-relaxed mb-4 md:mb-5 ${isDark ? 'text-gray-400' : 'text-[#0d1117]/50'}`}>
                    {t('consensus.performance.desc1')}
                  </p>

                  {/* 数据展示 */}
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 10000, label: t('consensus.performance.tps'), suffix: '+' },
                      { value: 2, label: t('consensus.performance.confirm'), prefix: '<', suffix: 's' },
                      { value: 99.9, label: t('consensus.performance.availability'), suffix: '%', decimals: 1 },
                    ].map((stat, index) => (
                      <div
                        key={index}
                        className={`rounded-lg md:rounded-xl p-2.5 md:p-3 text-center border ${
                          isDark ? 'bg-[#0d1117] border-white/10' : 'bg-white border-[#0d1117]/10'
                        }`}
                      >
                        <div className="text-lg md:text-xl font-bold bg-gradient-to-r from-[#E84A1F] to-[#F06030] bg-clip-text text-transparent">
                          <AnimatedCounter 
                            end={stat.value} 
                            prefix={stat.prefix || ''} 
                            suffix={stat.suffix} 
                            duration={2000}
                            decimals={stat.decimals || 0}
                          />
                        </div>
                        <div className={`text-[10px] mt-0.5 md:mt-1 ${isDark ? 'text-gray-500' : 'text-[#0d1117]/50'}`}>{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card3D>
            </div>

            {/* EVM 兼容性卡片 */}
            <div
              className={`relative group flex-1 transition-all duration-700 ${
                mainVisible ? 'opacity-100 translate-x-0' : 'opacity-0 lg:translate-x-10'
              }`}
              style={{ transitionDelay: '400ms' }}
            >
              <Card3D className="h-full" intensity={10} scale={1.03}>
                <div className="absolute inset-0 bg-gradient-to-br from-[#E84A1F]/10 to-transparent rounded-xl md:rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div
                  className={`relative border rounded-xl md:rounded-2xl p-4 md:p-6 h-full hover:border-[#E84A1F]/30 transition-all duration-500 shadow-lg ${
                    isDark
                      ? 'bg-[#161b22] border-white/10 shadow-black/20'
                      : 'bg-[#f8fafb] border-[#0d1117]/10 shadow-[#0d1117]/5'
                  }`}
                >
                  <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
                    <div className="w-10 md:w-12 h-10 md:h-12 bg-gradient-to-br from-[#E84A1F] to-[#C83A15] rounded-lg md:rounded-xl flex items-center justify-center">
                      <i className="ri-code-box-line text-xl md:text-2xl text-white"></i>
                    </div>
                    <h3
                      className={`text-base md:text-lg font-semibold transition-colors duration-500 ${
                        isDark ? 'text-white' : 'text-[#0d1117]'
                      }`}
                    >
                      {t('consensus.evm.title')}
                    </h3>
                  </div>

                  <p className={`text-xs md:text-sm leading-relaxed mb-4 md:mb-5 ${isDark ? 'text-gray-400' : 'text-[#0d1117]/50'}`}>
                    {t('consensus.evm.desc1')}
                  </p>

                  {/* 技术标签 */}
                  <div className="flex flex-wrap gap-1.5 md:gap-2">
                    {['Solidity', 'Web3.js', 'Ethers.js', 'Hardhat'].map((tech, index) => (
                      <div key={index} className="flex items-center gap-1 md:gap-1.5 px-2.5 md:px-3 py-1 md:py-1.5 bg-[#E84A1F]/10 border border-[#E84A1F]/20 rounded-full">
                        <i className="ri-checkbox-circle-fill text-[#E84A1F] text-xs md:text-sm"></i>
                        <span className={`text-[10px] md:text-xs ${isDark ? 'text-gray-300' : 'text-[#0d1117]/70'}`}>{tech}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card3D>
            </div>
          </div>
        </div>

        {/* 底部双卡片 */}
        <div ref={bottomRef} className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-4 md:mt-6">
          {/* 安全模型 */}
          <div
            className={`relative group transition-all duration-700 ${
              bottomVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <Card3D intensity={10} scale={1.02}>
              <div className="absolute inset-0 bg-gradient-to-br from-[#C83A15]/10 to-transparent rounded-xl md:rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div
                className={`relative border rounded-xl md:rounded-2xl p-5 md:p-8 hover:border-[#E84A1F]/30 transition-all duration-500 shadow-lg ${
                  isDark
                    ? 'bg-[#161b22] border-white/10 shadow-black/20'
                    : 'bg-[#f8fafb] border-[#0d1117]/10 shadow-[#0d1117]/5'
                }`}
              >
                <div className="flex items-start justify-between mb-4 md:mb-6">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-12 md:w-14 h-12 md:h-14 bg-gradient-to-br from-[#C83A15] to-[#E84A1F] rounded-lg md:rounded-xl flex items-center justify-center shadow-lg shadow-[#E84A1F]/20">
                      <i className="ri-lock-2-line text-xl md:text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className={`text-lg md:text-xl font-semibold transition-colors duration-500 ${isDark ? 'text-white' : 'text-[#0d1117]'}`}>{t('consensus.security.title')}</h3>
                      <p className={`text-[10px] md:text-xs mt-0.5 md:mt-1 ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>{t('consensus.security.desc1')}</p>
                    </div>
                  </div>
                </div>

                {/* 安全特性网格 */}
                <div className="grid grid-cols-3 gap-2 md:gap-3">
                  {[t('consensus.security.feature1'), t('consensus.security.feature2'), t('consensus.security.feature3')].map(
                    (feature, index) => (
                      <div
                        key={index}
                        className={`border rounded-lg md:rounded-xl p-3 md:p-4 text-center group/feature hover:border-[#E84A1F]/30 transition-all cursor-pointer ${
                          isDark ? 'bg-[#0d1117] border-white/10' : 'bg-white border-[#0d1117]/10'
                        }`}
                      >
                        <div className="w-8 md:w-10 h-8 md:h-10 bg-[#E84A1F]/10 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3 group-hover/feature:bg-[#E84A1F]/20 transition-colors">
                          <i className="ri-check-double-line text-[#E84A1F] text-base md:text-lg"></i>
                        </div>
                        <span className={`text-[10px] md:text-xs leading-tight block ${isDark ? 'text-gray-400' : 'text-[#0d1117]/60'}`}>{feature}</span>
                      </div>
                    )
                  )}
                </div>
              </div>
            </Card3D>
          </div>

          {/* 跨链桥 */}
          <div
            className={`relative group transition-all duration-700 ${
              bottomVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '150ms' }}
          >
            <Card3D intensity={10} scale={1.02}>
              <div className="absolute inset-0 bg-gradient-to-br from-[#F06030]/10 to-transparent rounded-xl md:rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div
                className={`relative border rounded-xl md:rounded-2xl p-5 md:p-8 hover:border-[#E84A1F]/30 transition-all duration-500 overflow-hidden shadow-lg ${
                  isDark
                    ? 'bg-[#161b22] border-white/10 shadow-black/20'
                    : 'bg-[#f8fafb] border-[#0d1117]/10 shadow-[#0d1117]/5'
                }`}
              >
                {/* 装饰性连接线 */}
                <div
                  className={`absolute top-1/2 right-8 -translate-y-1/2 hidden md:flex items-center gap-2 ${isDark ? 'opacity-30' : 'opacity-20'}`}
                >
                  <div className="w-3 h-3 rounded-full border-2 border-[#E84A1F]"></div>
                  <div className="w-12 lg:w-16 h-px bg-gradient-to-r from-[#E84A1F] to-transparent"></div>
                  <div className="w-3 h-3 rounded-full border-2 border-[#F06030]"></div>
                  <div className="w-12 lg:w-16 h-px bg-gradient-to-r from-[#F06030] to-transparent"></div>
                  <div className="w-3 h-3 rounded-full border-2 border-[#E84A1F]"></div>
                </div>

                <div className="flex flex-col md:flex-row items-start justify-between relative gap-4 md:gap-0">
                  <div className="max-w-full md:max-w-[65%]">
                    <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
                      <div className="w-12 md:w-14 h-12 md:h-14 bg-gradient-to-br from-[#F06030] to-[#C83A15] rounded-lg md:rounded-xl flex items-center justify-center shadow-lg shadow-[#E84A1F]/20">
                        <i className="ri-links-line text-xl md:text-2xl text-white"></i>
                      </div>
                      <div>
                        <h3 className={`text-lg md:text-xl font-semibold transition-colors duration-500 ${isDark ? 'text-white' : 'text-[#0d1117]'}`}>{t('consensus.crosschain.title')}</h3>
                        <p className={`text-[10px] md:text-xs mt-0.5 md:mt-1 ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>{t('consensus.crosschain.note')}</p>
                      </div>
                    </div>

                    <p className={`text-xs md:text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-[#0d1117]/50'}`}>{t('consensus.crosschain.desc')}</p>
                  </div>

                  {/* 数据指标 */}
                  <div className="flex md:flex-col gap-3 w-full md:w-auto">
                    <div className="flex-1 md:flex-none px-4 md:px-6 py-3 md:py-4 bg-gradient-to-br from-[#E84A1F]/10 to-[#E84A1F]/5 border border-[#E84A1F]/20 rounded-lg md:rounded-xl text-center backdrop-blur-sm">
                      <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#E84A1F] to-[#F06030] bg-clip-text text-transparent">
                        <AnimatedCounter end={5} suffix="+" duration={1500} />
                      </div>
                      <div className={`text-[10px] mt-0.5 md:mt-1 uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-[#0d1117]/50'}`}>{t('consensus.crosschain.chains')}</div>
                    </div>
                    <div className="flex-1 md:flex-none px-4 md:px-6 py-3 md:py-4 bg-gradient-to-br from-[#F06030]/10 to-[#F06030]/5 border border-[#F06030]/20 rounded-lg md:rounded-xl text-center backdrop-blur-sm">
                      <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#F06030] to-[#E84A1F] bg-clip-text text-transparent">
                        <AnimatedCounter end={100} suffix="%" duration={2000} />
                      </div>
                      <div className={`text-[10px] mt-0.5 md:mt-1 uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-[#0d1117]/50'}`}>{t('consensus.crosschain.verify')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Card3D>
          </div>
        </div>
      </div>

      {/* Shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </section>
  );
}
